import type { CmmdClaudeRuntimeReadiness } from "@cmmd-forge/contracts";
import { claudeReadinessReasonCode } from "@cmmd-forge/shared/claudeReadinessReason";

const TRANSITIONAL_CLAUDE_DELIVERY_STATUSES = new Set<CmmdClaudeRuntimeReadiness["deliveryStatus"]>(
  ["pending", "claimed", "activating"],
);

export const MAX_CONSECUTIVE_UNAVAILABLE_READINESS_RESPONSES = 3;

/**
 * How often the Shell reads Claude readiness while it polls. CMMD retries a
 * failed refresh only when a read finds it due, so this poll is what drives
 * those retries (FOR-3084).
 */
export const CLAUDE_READINESS_POLL_INTERVAL_MS = 2_000;

/**
 * A backstop, not the stop signal. A current CMMD ends the poll itself: once
 * it stops attempting a delivery it answers the reconnect reason (FOR-3085).
 * This cap is for a CMMD that never sends that reason, so the poll cannot
 * read a failed answer forever.
 *
 * The bound it gives: at most this many failed answers in a row, so the poll
 * stops (MAX_CONSECUTIVE_FAILED_READINESS_RESPONSES - 1) times
 * CLAUDE_READINESS_POLL_INTERVAL_MS after the first one, thirty seconds. Any
 * other status resets the count, and a retry CMMD dispatches reads as claimed
 * or activating, so retries that Sprite exec time stretches to a few minutes
 * keep the poll going. Thirty seconds equals FOR-3084's 2, 4, 8 and 16 second
 * waits, so the count can run out before CMMD's last retry only if no read
 * lands while any retry is in flight.
 */
export const MAX_CONSECUTIVE_FAILED_READINESS_RESPONSES = 16;

/** What one provider access response says, as far as the readiness poll reads it. */
export interface ClaudeReadinessPollingSnapshot {
  readonly claudeOffered: boolean;
  readonly claudeConnected: boolean;
  readonly deliveryStatus: CmmdClaudeRuntimeReadiness["deliveryStatus"] | null;
  readonly reason: string | null;
  readonly readinessUnavailable: boolean;
}

export interface ClaudeReadinessPollingState {
  /**
   * The readiness target this poll belongs to, whichever id shape the ask was
   * keyed by (legacy runtime id or durable `env_` id). A change resets the
   * counters: one poll per target, no carry-over.
   */
  readonly readinessTargetId: string | null;
  readonly dataUpdatedAt: number;
  readonly consecutiveUnavailableResponses: number;
  readonly consecutiveFailedResponses: number;
}

export function initialClaudeReadinessPollingState(
  readinessTargetId: string | null,
): ClaudeReadinessPollingState {
  return {
    readinessTargetId,
    dataUpdatedAt: 0,
    consecutiveUnavailableResponses: 0,
    consecutiveFailedResponses: 0,
  };
}

export function advanceClaudeReadinessPollingState(input: {
  readonly current: ClaudeReadinessPollingState;
  readonly readinessTargetId: string | null;
  readonly dataUpdatedAt: number;
  readonly readinessUnavailable: boolean;
  readonly deliveryStatus: CmmdClaudeRuntimeReadiness["deliveryStatus"] | null;
}): ClaudeReadinessPollingState {
  const current =
    input.current.readinessTargetId === input.readinessTargetId
      ? input.current
      : initialClaudeReadinessPollingState(input.readinessTargetId);
  if (input.dataUpdatedAt === 0 || input.dataUpdatedAt === current.dataUpdatedAt) {
    return current;
  }
  return {
    readinessTargetId: input.readinessTargetId,
    dataUpdatedAt: input.dataUpdatedAt,
    consecutiveUnavailableResponses: input.readinessUnavailable
      ? current.consecutiveUnavailableResponses + 1
      : 0,
    consecutiveFailedResponses:
      input.deliveryStatus === "failed" ? current.consecutiveFailedResponses + 1 : 0,
  };
}

export function shouldRefetchClaudeRuntimeReadiness(
  snapshot: ClaudeReadinessPollingSnapshot,
  pollingState: ClaudeReadinessPollingState,
): boolean {
  if (!snapshot.claudeOffered || !snapshot.claudeConnected) return false;
  const reasonCode = claudeReadinessReasonCode(snapshot.reason);
  // A refresh is under way whatever the status says: CMMD answers applied
  // while a refresh this read found out of date is not queued yet (FOR-3085).
  if (reasonCode === "CREDENTIAL_EXPIRED_REFRESHING") return true;
  if (snapshot.deliveryStatus === "failed") {
    // CMMD has stopped attempting the delivery (FOR-3085). No later read can
    // drive another attempt, and the server guard refuses the turn on the same
    // reasons, so the poll ends on the first answer that names one.
    if (reasonCode === "CREDENTIAL_RECONNECT_REQUIRED") return false;
    // Backstop only, for a CMMD that never sends the reconnect reason (older deploys).
    return pollingState.consecutiveFailedResponses < MAX_CONSECUTIVE_FAILED_READINESS_RESPONSES;
  }
  if (snapshot.deliveryStatus !== null) {
    return TRANSITIONAL_CLAUDE_DELIVERY_STATUSES.has(snapshot.deliveryStatus);
  }
  return (
    pollingState.consecutiveUnavailableResponses > 0 &&
    pollingState.consecutiveUnavailableResponses < MAX_CONSECUTIVE_UNAVAILABLE_READINESS_RESPONSES
  );
}
